import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1 },
  }),
};

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent successfully!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-24 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left side info */}
          <div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              custom={0}
            >
              <span className="text-[#F59E0B] text-sm font-semibold tracking-[0.3em] uppercase">Get in Touch</span>
            </motion.div>
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              custom={1}
              className="font-['Bebas_Neue'] text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-4 mb-6"
            >
              Ready to <span className="text-[#F59E0B]">Start?</span>
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              custom={2}
              className="text-[#A3A3A3] text-base leading-relaxed mb-10 max-w-md"
            >
              Drop us a message and we'll get back to you within 24 hours. Whether you're looking for a membership, have questions about our programs, or want to schedule a tour — we're here.
            </motion.p>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              custom={3}
              className="space-y-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F59E0B]/10 flex items-center justify-center border border-[#F59E0B]/20">
                  <span className="text-[#F59E0B] text-lg">01</span>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Fill the form</p>
                  <p className="text-[#A3A3A3] text-xs">Tell us about your goals</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F59E0B]/10 flex items-center justify-center border border-[#F59E0B]/20">
                  <span className="text-[#F59E0B] text-lg">02</span>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">We reach out</p>
                  <p className="text-[#A3A3A3] text-xs">Our team connects within 24hrs</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F59E0B]/10 flex items-center justify-center border border-[#F59E0B]/20">
                  <span className="text-[#F59E0B] text-lg">03</span>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Start training</p>
                  <p className="text-[#A3A3A3] text-xs">Begin your transformation</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            custom={2}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-12 border border-[#262626] bg-[#121212]">
                <CheckCircle className="w-16 h-16 text-[#F59E0B] mb-6" />
                <h3 className="font-['Bebas_Neue'] text-3xl text-white mb-3">Message Sent!</h3>
                <p className="text-[#A3A3A3] text-sm mb-6">We'll get back to you within 24 hours.</p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-3 bg-[#F59E0B] text-black font-semibold text-sm tracking-wider uppercase hover:bg-[#D97706] transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={handleSubmit}
                className="space-y-6 p-8 border border-[#262626] bg-[#121212]"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm text-[#A3A3A3] tracking-wider uppercase">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="bg-[#0A0A0A] border-[#262626] text-white placeholder:text-[#555] focus:ring-[#F59E0B] focus:border-[#F59E0B] rounded-none h-12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm text-[#A3A3A3] tracking-wider uppercase">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@email.com"
                      className="bg-[#0A0A0A] border-[#262626] text-white placeholder:text-[#555] focus:ring-[#F59E0B] focus:border-[#F59E0B] rounded-none h-12"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm text-[#A3A3A3] tracking-wider uppercase">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="contact-phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXXXXXXX"
                    className="bg-[#0A0A0A] border-[#262626] text-white placeholder:text-[#555] focus:ring-[#F59E0B] focus:border-[#F59E0B] rounded-none h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm text-[#A3A3A3] tracking-wider uppercase">
                    Message *
                  </Label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your fitness goals..."
                    rows={5}
                    className="flex w-full bg-[#0A0A0A] border border-[#262626] text-white placeholder:text-[#555] focus:ring-[#F59E0B] focus:border-[#F59E0B] focus:outline-none px-3 py-3 text-sm"
                    required
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-4 bg-[#F59E0B] text-black font-semibold text-sm tracking-wider uppercase hover:bg-[#D97706] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02]"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send size={16} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
